import {
  MLSListing,
  StandardStatus,
  ToolkitListingID,
  ToolkitPropertyEnum,
  Transaction,
  expected,
  isDefined,
  isToolkitListingID,
  listingStatusToUADStatus,
  parseToolkitListingID,
} from '@aloft/toolkit-shared';
import { UseQueryResult } from '@tanstack/react-query';

import { Coordinates } from '../../../components/map/types.ts';
import { ListingResult, isFoundListing } from '../../../controller/hooks/listings/types.ts';
import { useGetListings } from '../../../controller/hooks/listings/useGetListings.ts';
import {
  ListingIDsByPropertyID,
  useListingIDsByPropertyID,
} from '../../../controller/hooks/useListingIDsByPropertyID.tsx';
import { useTkv2ReferenceBarUserInterface } from '../../../controller/hooks/useTkv2ReferenceBarUserInterface.ts';
import { useGetCoordinatesForAddressesAPI } from '../../../hooks/api/schema-hooks/useGetCoordinatesForAddressesAPI.ts';
import { selectTransactionEntities } from '../../../redux/features/selectors/propertyDataSelectors/transactionSelectors.ts';
import { selectReferenceBarUI } from '../../../redux/features/selectors/referenceBarUISelectors.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { useCompSequence } from '../Tkv2MlsMap/useCompSequence.ts';
import { useSubjectPinCoordinates } from '../Tkv2MlsMap/useSubjectPinCoordinates.ts';
import { AddressByPropertyID, useAddressesByPropertyID } from './useAddressesByPropertyID.ts';

export type MapData = Coordinates & {
  listing?: MLSListing;
  mlsStatus?: ToolkitPropertyEnum<'ListingStatusType'>;
};

const mapDataForPropertyLookup =
  (
    listingIDsByPropertyID: ListingIDsByPropertyID,
    listings: UseQueryResult<ListingResult>[],
    transactions: Record<string, Transaction>,
    addressesByPropertyId: AddressByPropertyID,
    latLngs:
      | {
          address: string;
          lat?: number | undefined;
          lng?: number | undefined;
        }[]
      | undefined,
    subjectPinCoordinates: ReturnType<typeof useSubjectPinCoordinates>,
  ) =>
  (propertyID: string): MapData => {
    const listingResult = listings.find((result) => {
      if (isDefined(result.data) && isFoundListing(result.data)) {
        const targetID = listingIDsByPropertyID[propertyID] as ToolkitListingID;
        const listingID = result.data.listing.ToolkitListingID as ToolkitListingID;

        const targetParsed = parseToolkitListingID(targetID);
        const listingParsed = parseToolkitListingID(listingID);
        if (!targetParsed || !listingParsed) return false;
        return (
          targetParsed.mls === listingParsed.mls &&
          targetParsed.listingID === listingParsed.listingID
        );
      }
      return false;
    });
    const listing =
      isDefined(listingResult) &&
      isDefined(listingResult.data) &&
      isFoundListing(listingResult.data)
        ? listingResult.data.listing
        : undefined;

    const address = addressesByPropertyId[propertyID];

    let latLngResult = latLngs?.find((result) => result.address === address);

    if (propertyID === 'subject' && subjectPinCoordinates.coordinates) {
      latLngResult = {
        address: address ?? '',
        lat: subjectPinCoordinates.coordinates.lat,
        lng: subjectPinCoordinates.coordinates.lng,
      };
    }

    // Prefer google geocode lat/lng, but fall back to listing lat/lng
    const coords = isDefined(latLngResult)
      ? {
          lat: latLngResult?.lat ?? 0,
          lng: latLngResult?.lng ?? 0,
        }
      : {
          lat: listing?.Latitude ?? 0,
          lng: listing?.Longitude ?? 0,
        };

    return {
      ...coords,
      mlsStatus:
        transactions?.[propertyID]?.status ||
        (listingStatusToUADStatus(
          listing?.StandardStatus as StandardStatus,
        ) as ToolkitPropertyEnum<'ListingStatusType'>),
      listing,
    };
  };

export type MapCoords = {
  isLoading: boolean;
  results: { [key: string]: MapData };
};

export const useMapCoords = (): MapCoords => {
  const { inPairedSales } = useTkv2ReferenceBarUserInterface();
  const { properties } = useAppSelector(selectReferenceBarUI);
  const compSequence = useCompSequence();
  const transactions = useAppSelector(selectTransactionEntities);

  const listingIDsByPropertyID = useListingIDsByPropertyID();

  const pairedSalesListingIDs = properties
    .filter((property) => isToolkitListingID(property.id))
    .map((property) => property.id as ToolkitListingID);

  const listings = useGetListings(
    Object.values(listingIDsByPropertyID).concat(pairedSalesListingIDs).filter(isDefined),
  );
  const addresses = useAddressesByPropertyID();
  const subjectPinCoordinates = useSubjectPinCoordinates();

  const pairedSalesAddresses: AddressByPropertyID = { subject: expected(addresses.subject) };
  pairedSalesListingIDs.forEach((id) => {
    const listingResult = listings.find(
      (result) =>
        !result.isLoading &&
        isDefined(result.data) &&
        isFoundListing(result.data) &&
        result.data.listing.ToolkitListingID === id,
    );

    const { data } = listingResult ?? {};
    if (!data || !isFoundListing(data)) return;

    const listingData = data.listing;
    const toolkitListingID = expected(listingData.ToolkitListingID);
    pairedSalesAddresses[toolkitListingID] =
      `${listingData?.UnparsedAddress} ${listingData?.City}, ${listingData?.StateOrProvince} ${listingData?.PostalCode}`;

    listingIDsByPropertyID[toolkitListingID] = toolkitListingID;
  });

  const addressesToUse = inPairedSales ? pairedSalesAddresses : addresses;

  const { data: latLngData, isLoading: latLngIsLoading } = useGetCoordinatesForAddressesAPI({
    queryParams: {
      addresses: Object.values(addressesToUse).map((addr) => encodeURIComponent(addr)),
    },
  });

  const getMapData = mapDataForPropertyLookup(
    listingIDsByPropertyID,
    listings,
    transactions,
    addressesToUse,
    latLngData,
    subjectPinCoordinates,
  );

  const results: { [key: string]: MapData } = {
    subject: getMapData('subject'),
  };
  compSequence.forEach((comp) => {
    results[comp] = getMapData(comp);
  });
  pairedSalesListingIDs.forEach((id) => {
    results[id] = getMapData(id);
  });

  const isLoading = listings.filter((result) => result.isLoading).length > 0 || latLngIsLoading;

  return {
    isLoading,
    results: isLoading ? {} : results,
  };
};
